import React from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import InvestmentDisclaimer from "@/components/InvestmentDisclaimer";

const headlineMetrics = [
  { v: "120", suffix: "Acres", l: "Phase I Platform" },
  { v: "6,000", suffix: "Tonnes", l: "Annual tomato production" },
  { v: "1M kg", suffix: "Products", l: "Finished products annually" },
  { v: "CARICOM", suffix: "", l: "Regional export markets" },
];

const opportunityPillars = [
  { t: "Food Sovereignty", b: "Reduce dependence on imports and strengthen national food security through local production and processing." },
  { t: "Foreign Exchange Retention", b: "Retain millions in foreign exchange annually by substituting imports with local value-added processing." },
  { t: "Job Creation & Farmer Participation", b: "Create jobs across farming, processing, logistics and distribution while empowering local farmers and communities." },
  { t: "CARICOM Export Opportunity", b: "Establish a competitive platform to serve regional markets and capture export opportunities." },
  { t: "Government Alignment", b: "Aligned with national priorities for food security, economic resilience and sustainable agricultural development." },
];

const importDependency = [
  { v: "6,000+ tonnes", l: "Tomatoes & tomato products imported annually" },
  { v: "USD $35M+", l: "Estimated annual spend on tomato products in CARICOM" },
  { v: "High FX outflow", l: "Value leaving the economy instead of being retained locally" },
];

const valueChain = [
  {
    title: "Farmers & Production",
    items: ["Anchor landholder (~50 acres)", "Farmer participation & outgrower network", "Good agricultural practices (GAP)", "Year-round production planning"],
  },
  {
    title: "Processing Facility",
    items: ["Modern processing facility", "Sorting, washing, blanching & crushing", "Quality control & food safety", "Efficient, scalable operations"],
  },
  {
    title: "Value-Added Products",
    items: ["Sauces, concentrates, juices & more", "Sustainable packaging solutions", "Consistent quality & brand development", "Higher value creation locally"],
  },
  {
    title: "Domestic Distribution",
    items: ["Retailers, restaurants & institutions", "Reliable cold chain & logistics", "Local brands & consumer trust", "Strengthening food security"],
  },
  {
    title: "CARICOM Export Markets",
    items: ["Access to CARICOM markets", "Competitive quality & pricing", "Export compliance & certification", "Growth beyond our shores"],
  },
];

const phases = [
  { p: "Phase 1", r: "0–6 months", t: "Foundation & Setup", items: ["SPV establishment", "Anchor landholder agreement (~50 acres)", "Detailed feasibility study", "Financing roadmap & term sheet", "Strategic partner engagement"] },
  { p: "Phase 2", r: "6–12 months", t: "Design & Financing", items: ["Engineering & facility design", "Environmental & social assessments", "Secure financing commitments", "Offtake & supply agreements", "Operating model finalisation"] },
  { p: "Phase 3", r: "12–24 months", t: "Construction & Setup", items: ["Site works & civil construction", "Equipment procurement & installation", "Farm development & planting", "Recruitment & training", "Commissioning planning"] },
  { p: "Phase 4", r: "24–30 months", t: "Commissioning & Ramp-Up", items: ["Facility commissioning", "Production trials", "Quality systems implementation", "Market readiness", "Initial commercial production"] },
  { p: "Phase 5", r: "30–36 months", t: "Full Operations & Expansion", items: ["Full scale operations", "Market expansion (regional & export)", "Process optimisation", "Value-added product development", "Phase II expansion planning"] },
];

const impactMetrics = [
  { v: "6,000 tonnes", l: "Annual tomato production" },
  { v: "1,000,000 kg", l: "Finished products annually" },
  { v: "22–25%", l: "EBITDA margin (stabilised)" },
  { v: "XCD $38.4M+", l: "Annual revenue (Year 3 stabilised)" },
  { v: "250+", l: "Direct & indirect jobs created" },
  { v: "3.5–4.0 yrs", l: "Breakeven (post completion)" },
];

const useOfFunds = [
  { v: "$5.2M", pct: "28%", l: "Agriculture & Production" },
  { v: "$9.4M", pct: "50%", l: "Processing Facility" },
  { v: "$2.8M", pct: "15%", l: "Distribution & Export" },
  { v: "$1.5M", pct: "7%", l: "Working Capital & Other" },
];

const fundingMix = [
  { v: "30%", l: "Strategic Equity" },
  { v: "35%", l: "Development Finance" },
  { v: "25%", l: "Senior Debt" },
  { v: "10%", l: "Government Incentives" },
];

const leadership = [
  {
    name: "Jason Kehoe",
    role: "Founder & Executive Chairman",
    org: "Evergreen Global Holdings Ltd",
    quote: "Responsible for establishing and leading Evergreen Global Holdings Ltd and sponsoring the Platform.",
    tags: ["Project Sponsor", "Capital Formation", "Strategic Direction", "Investor Engagement"],
  },
  {
    name: "Ian Jarvis",
    role: "Executive Chairman",
    org: "Agro-Industrial Platform",
    quote: "Responsible for development of the integrated investment platform and investor readiness programme.",
    tags: ["Platform Strategy", "Governance", "Investment Memorandum", "DFI Engagement"],
  },
  {
    name: "Anchor Agricultural Partner",
    role: "Landholder & Agricultural Steward",
    org: "Saint Vincent & the Grenadines",
    quote: "Provides agricultural experience, land access and commitment to long-term production.",
    tags: ["~50 Acres", "Farmer Network", "Supply Chain Support", "Local Coordination"],
  },
  {
    name: "Charles Giles",
    role: "Government & Stakeholder Relations",
    org: "",
    quote: "Leads government engagement and stakeholder alignment to support platform development and growth.",
    tags: ["Government Engagement", "Regulatory Liaison", "Strategic Partnerships", "Community Alignment"],
  },
  {
    name: "Evergreen Operating Team",
    role: "Operations, Finance & Development",
    org: "",
    quote: "Executes day-to-day operations, financial management and project delivery to drive results.",
    tags: ["SPV Administration", "Commercial Operations", "Reporting & Compliance", "Project Delivery"],
  },
];

const brandImages = [
  { src: "https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/lw6mme28_IMG_0625.jpeg", caption: "Cultivation" },
  { src: "https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/6wx54trf_IMG_0623.jpeg", caption: "Production" },
  { src: "https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/2pownbf7_IMG_0624.jpeg", caption: "Processing Facility" },
  { src: "https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/n1zs08sn_IMG_0626.jpeg", caption: "Value-Added Output" },
];

export default function AgroIndustrial() {
  return (
    <>
      <PageHeader
        testId="agro-header"
        eyebrow="Strategic Platform · 02"
        title="National Agro-Industrial Import Substitution Platform — Tomato Phase I"
        intro="A strategically led platform to produce, process and export high-quality tomato products — reducing imports, retaining foreign exchange and building a resilient, food-secure future for Saint Vincent & the Grenadines."
        image="https://customer-assets.emergentagent.com/job_evergreen-holdings/artifacts/2pownbf7_IMG_0624.jpeg"
        meta="Saint Vincent & the Grenadines · Tomato Phase I"
      />

      {/* Headline Metrics */}
      <section data-testid="agro-metrics" className="bg-white border-b border-[#0A1F44]/12">
        <div className="evg-container py-16 md:py-20">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12">
            {headlineMetrics.map((m, i) => (
              <div key={m.l} data-reveal data-reveal-delay={String(i * 100)}>
                <div className="font-serif text-4xl md:text-5xl text-[#0A1F44] tracking-tight">
                  {m.v}
                  {m.suffix ? (
                    <span className="text-base ml-2 text-[#0A1F44]/55 font-sans tracking-normal">
                      {m.suffix}
                    </span>
                  ) : null}
                </div>
                <div className="evg-overline mt-4">{m.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Imagery Band — photography-first */}
      <section data-testid="agro-imagery" className="bg-white">
        <div className="evg-container py-20 md:py-28">
          <div className="evg-overline mb-10" data-reveal>Platform in View</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {brandImages.map((img, i) => (
              <figure
                key={img.caption}
                data-reveal
                data-reveal-delay={String(i * 100)}
                className="relative evg-img-zoom"
              >
                <div className="aspect-[4/5] bg-[#0A1F44] overflow-hidden">
                  <img
                    src={img.src}
                    alt={img.caption}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <figcaption className="mt-4 evg-overline-gold">{img.caption}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* The Opportunity */}
      <section data-testid="agro-opportunity" className="evg-section bg-white">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-12">
            <div className="lg:col-span-5" data-reveal>
              <div className="evg-overline mb-8">The Opportunity</div>
              <h2 className="evg-display-lg">Building food sovereignty and retaining value.</h2>
              <p className="evg-lead mt-8 max-w-md">
                We can produce. We can process. We can retain value locally.
              </p>
            </div>
            <div className="lg:col-span-7">
              {opportunityPillars.map((p, i) => (
                <div
                  key={p.t}
                  data-testid={`agro-pillar-${i + 1}`}
                  data-reveal
                  data-reveal-delay={String((i % 3) * 100)}
                  className="py-9 border-t border-[#0A1F44]/12 last:border-b last:border-[#0A1F44]/12 grid grid-cols-12 gap-x-8 gap-y-3 items-baseline"
                >
                  <span className="col-span-12 md:col-span-1 evg-index">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="col-span-12 md:col-span-4 font-serif text-2xl">{p.t}</h3>
                  <p className="col-span-12 md:col-span-7 text-[15px] text-[#0A1F44]/72 leading-relaxed font-light">
                    {p.b}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Import Dependency */}
          <div className="mt-32" data-reveal>
            <div className="evg-overline mb-10">The Import Dependency</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-10">
              {importDependency.map((d, i) => (
                <div key={d.l} className="border-t border-[#D4AF37] pt-8" data-reveal data-reveal-delay={String(i * 100)}>
                  <div className="font-serif text-4xl text-[#0A1F44] tracking-tight">{d.v}</div>
                  <div className="mt-4 text-[14px] text-[#0A1F44]/65 leading-relaxed font-light">{d.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Starting Advantage — editorial 2-col */}
          <div className="mt-28 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14" data-reveal>
            <div className="border-t border-[#0A1F44]/12 pt-10">
              <div className="evg-overline mb-6">Our Starting Advantage</div>
              <h3 className="evg-display-sm">~50 Acres Anchor Landholder Committed</h3>
              <p className="mt-6 evg-body text-[15px]">
                Land access secured to drive Phase I production. Strong agricultural experience
                and long-term commitment provide the foundation for farmer participation and
                outgrower development.
              </p>
            </div>
            <div className="border-t border-[#0A1F44]/12 pt-10">
              <div className="evg-overline mb-6">Platform Footprint</div>
              <h3 className="evg-display-sm">120 Acres Phase I Platform</h3>
              <p className="mt-6 evg-body text-[15px]">
                Integrated production, processing and distribution platform delivering sustainable
                local production, value-added processing and domestic market supply.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Value Chain — editorial flow, no boxed cards */}
      <section data-testid="agro-value-chain" className="evg-section bg-[#0A1F44] text-white">
        <div className="evg-container">
          <div className="max-w-3xl pb-20" data-reveal>
            <div className="evg-overline-gold mb-8">Platform Overview</div>
            <h2 className="evg-display-lg text-white">An integrated value chain from farm to market.</h2>
            <p className="mt-8 text-white/72 text-[17px] leading-[1.8] font-light max-w-2xl">
              A fully integrated platform designed to produce, process and distribute high-quality
              tomato products for the domestic market and CARICOM export.
            </p>
          </div>

          <div>
            {valueChain.map((step, i) => (
              <div
                key={step.title}
                data-testid={`agro-chain-${i + 1}`}
                data-reveal
                data-reveal-delay={String(i * 80)}
                className="py-10 md:py-12 border-t border-white/15 last:border-b last:border-white/15 grid grid-cols-12 gap-x-8 gap-y-4"
              >
                <span className="col-span-12 md:col-span-1 evg-index evg-index-light">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="col-span-12 md:col-span-4 font-serif text-2xl md:text-3xl text-white">
                  {step.title}
                </h3>
                <ul className="col-span-12 md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  {step.items.map((it) => (
                    <li key={it} className="text-[14px] text-white/72 leading-relaxed font-light">
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Opportunity */}
      <section data-testid="agro-investment" className="evg-section bg-white">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-12">
            <div className="lg:col-span-5" data-reveal>
              <div className="evg-overline mb-8">Investment Opportunity</div>
              <h2 className="evg-display-lg">
                Food sovereignty. Foreign exchange retention. Regional growth.
              </h2>
              <p className="evg-lead mt-8 max-w-md">
                A capital-efficient, impact-driven platform positioned to deliver strong financial
                returns while advancing national and regional development goals.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10" data-reveal data-reveal-delay="200">
              <div className="border-t border-[#D4AF37] pt-8">
                <div className="evg-overline mb-4">Total Development Capital</div>
                <div className="font-serif text-5xl md:text-6xl text-[#0A1F44] tracking-tight">XCD $18.9M</div>
                <p className="mt-6 text-[14px] text-[#0A1F44]/65 leading-relaxed font-light">
                  Capital deployed across agriculture, processing, distribution and working
                  capital to deliver integrated value.
                </p>
              </div>
              <div className="border-t border-[#D4AF37] pt-8">
                <div className="evg-overline mb-4">Seeking Equity Investment</div>
                <div className="font-serif text-5xl md:text-6xl text-[#0A1F44] tracking-tight">XCD $5.7M</div>
                <p className="mt-6 text-[14px] text-[#0A1F44]/65 leading-relaxed font-light">
                  Equity capital will drive development, unlock concessional finance and create
                  long-term, sustainable value.
                </p>
              </div>
            </div>
          </div>

          {/* Use of Funds + Funding Mix */}
          <div className="mt-28 grid grid-cols-1 lg:grid-cols-2 gap-x-20 gap-y-16" data-reveal>
            <div>
              <div className="evg-overline mb-8">Use of Funds</div>
              <div>
                {useOfFunds.map((u) => (
                  <div key={u.l} className="py-5 border-t border-[#0A1F44]/12 last:border-b last:border-[#0A1F44]/12 flex items-baseline justify-between gap-6">
                    <span className="font-serif text-xl md:text-2xl text-[#0A1F44]">{u.l}</span>
                    <span className="text-right whitespace-nowrap">
                      <span className="font-serif text-xl md:text-2xl text-[#0A1F44]">{u.v}</span>
                      <span className="ml-3 evg-overline-gold">{u.pct}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="evg-overline mb-8">Funding Structure (Indicative)</div>
              <div>
                {fundingMix.map((f) => (
                  <div key={f.l} className="py-5 border-t border-[#0A1F44]/12 last:border-b last:border-[#0A1F44]/12 flex items-baseline justify-between gap-6">
                    <span className="font-serif text-xl md:text-2xl text-[#0A1F44]">{f.l}</span>
                    <span className="font-serif text-xl md:text-2xl text-[#0A1F44]">{f.v}</span>
                  </div>
                ))}
              </div>
              <p className="mt-8 text-[14px] text-[#0A1F44]/65 leading-relaxed font-light">
                Blended finance structure designed to optimise cost of capital, mitigate risk and
                maximise impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Financial Projections */}
      <section data-testid="agro-projections" className="evg-section bg-[#F5F6F8]">
        <div className="evg-container">
          <div className="max-w-3xl pb-16" data-reveal>
            <div className="evg-overline mb-8">Key Financial Projections — Phase I</div>
            <h2 className="evg-display-lg">Attractive returns. Disciplined operations.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-12 gap-y-14">
            {impactMetrics.map((m, i) => (
              <div
                key={m.l}
                data-testid={`agro-impact-${i + 1}`}
                data-reveal
                data-reveal-delay={String((i % 3) * 100)}
                className="border-t border-[#D4AF37] pt-7"
              >
                <div className="font-serif text-3xl md:text-4xl text-[#0A1F44] tracking-tight">{m.v}</div>
                <div className="mt-4 evg-overline">{m.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap — editorial phases */}
      <section data-testid="agro-roadmap" className="evg-section bg-white">
        <div className="evg-container">
          <div className="max-w-3xl pb-20" data-reveal>
            <div className="evg-overline mb-8">Development Roadmap (Phase I — 36 Months)</div>
            <h2 className="evg-display-lg">A phased approach. Clear milestones. Sustainable results.</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-x-10 gap-y-12">
            {phases.map((p, i) => (
              <div
                key={p.p}
                data-testid={`agro-phase-${i + 1}`}
                data-reveal
                data-reveal-delay={String(i * 80)}
                className="border-t border-[#D4AF37] pt-7"
              >
                <div className="evg-overline-gold">{p.p}</div>
                <p className="evg-meta mt-2">{p.r}</p>
                <h3 className="font-serif text-2xl mt-4 leading-tight">{p.t}</h3>
                <ul className="mt-6 space-y-2.5">
                  {p.items.map((it) => (
                    <li key={it} className="text-[13.5px] text-[#0A1F44]/72 leading-relaxed font-light">
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section data-testid="agro-leadership" className="evg-section bg-[#F5F6F8]">
        <div className="evg-container">
          <div className="max-w-3xl pb-20" data-reveal>
            <div className="evg-overline mb-8">Leadership, Governance & Execution</div>
            <h2 className="evg-display-lg">The team driving the national agro-industrial platform.</h2>
            <p className="evg-lead mt-8 max-w-2xl">
              A proven leadership team combining capital expertise, governance, government
              engagement and agricultural stewardship to deliver long-term value for Saint Vincent
              &amp; the Grenadines.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
            {leadership.map((p, i) => (
              <div
                key={p.name}
                data-testid={`agro-leader-${p.name.split(" ")[0].toLowerCase()}`}
                data-reveal
                data-reveal-delay={String((i % 2) * 120)}
                className="pt-10 border-t border-[#0A1F44]/12"
              >
                <h3 className="font-serif text-3xl text-[#0A1F44] leading-tight">{p.name}</h3>
                <p className="mt-3 text-[14px] text-[#0A1F44]/75">{p.role}</p>
                {p.org ? <p className="text-[12px] text-[#0A1F44]/55 mt-1">{p.org}</p> : null}
                <p className="mt-6 text-[14px] italic text-[#0A1F44]/70 leading-[1.8] font-light">
                  “{p.quote}”
                </p>
                <div className="mt-7 flex flex-wrap gap-x-5 gap-y-2">
                  {p.tags.map((t) => (
                    <span key={t} className="evg-meta">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + Disclaimer */}
      <section data-testid="agro-cta" className="evg-section bg-white border-t border-[#0A1F44]/12">
        <div className="evg-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-16 gap-y-12 items-end">
            <div className="lg:col-span-8" data-reveal>
              <div className="evg-overline mb-8">Investor Opportunity</div>
              <h2 className="evg-display-lg">Request the confidential investor teaser.</h2>
              <p className="evg-lead mt-8 max-w-xl">
                Local production. Local value. Global potential.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-6 lg:items-end" data-reveal data-reveal-delay="200">
              <a
                href="/documents/Evergreen_Tomato_Phase_I_Teaser.pdf"
                target="_blank"
                rel="noreferrer noopener"
                data-testid="agro-download-teaser"
                className="text-[11px] uppercase tracking-[0.24em] font-medium text-[#0A1F44] border-b border-[#0A1F44] pb-2 hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors inline-flex items-center gap-2 self-start lg:self-end"
              >
                <Download size={14} /> Download Teaser
              </a>
              <Link
                to="/investor-relations"
                data-testid="agro-cta-link"
                className="text-[11px] uppercase tracking-[0.24em] font-medium text-[#0A1F44] border-b border-[#D4AF37] pb-2 hover:text-[#D4AF37] transition-colors inline-block self-start lg:self-end"
              >
                Discuss Investment →
              </Link>
            </div>
          </div>
          <div className="mt-24 pt-12 border-t border-[#0A1F44]/12">
            <InvestmentDisclaimer />
          </div>
        </div>
      </section>
    </>
  );
}
